import type { UserRole } from './access-role.js';
import { SECTION_CATALOG, type SectionId } from '../sections/section-catalog.js';

// The single catalog used by HTTP authorization, navigation and the access editor.
export const SECTION_OPERATIONS = {
  positions: ['view', 'create', 'edit', 'delete', 'import', 'run'],
  metrics: ['view', 'create', 'edit', 'delete', 'settings', 'run'],
  alerts: ['view', 'settings', 'schedule', 'run', 'send'],
  reports: ['view', 'create', 'delete', 'import', 'run'],
  registry: ['view', 'create', 'edit', 'delete', 'import'],
  donors: [
    'view',
    'create',
    'edit',
    'delete',
    'import',
    'run',
    'stop',
    'settings',
    'schedule',
    'send',
  ],
  fakes: [
    'view',
    'create',
    'edit',
    'import',
    'export',
    'run',
    'stop',
    'settings',
    'schedule',
    'send',
  ],
  jobs: ['view'],
  telegram: ['view', 'create', 'edit', 'settings', 'schedule', 'run', 'publish'],
  shorts: ['view', 'create', 'edit', 'publish'],
  settings: ['view', 'create'],
  documentation: ['view', 'export'],
} as const satisfies Record<SectionId, readonly string[]>;

export const OPERATION_LABELS = {
  view: 'Просмотр',
  create: 'Создание',
  edit: 'Редактирование',
  delete: 'Удаление',
  import: 'Импорт',
  export: 'Экспорт',
  run: 'Запуск',
  stop: 'Остановка',
  settings: 'Настройки',
  schedule: 'Расписания',
  publish: 'Публикация',
  send: 'Отправка',
} as const;
export type Permission = {
  [S in SectionId]: `${S}.${(typeof SECTION_OPERATIONS)[S][number]}`;
}[SectionId];
export type PermissionOverrides = Partial<Record<Permission, boolean>>;
export interface AccessPolicy {
  grantId: string;
  revision: number;
  overrides: PermissionOverrides;
}
export interface PermissionSubject {
  role: UserRole;
  access: AccessPolicy;
}
export const ADMIN_POLICY: AccessPolicy = { grantId: 'environment', revision: 0, overrides: {} };
export const PERMISSIONS = SECTION_CATALOG.flatMap(({ id }) =>
  SECTION_OPERATIONS[id].map((operation) => ({
    key: `${id}.${operation}` as Permission,
    section: id,
    operation,
    label: OPERATION_LABELS[operation],
    viewer: operation === 'view' || operation === 'export',
  })),
);
const catalog = new Map(PERMISSIONS.map((p) => [p.key, p]));

export function validateOverrides(value: unknown): PermissionOverrides {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    Object.entries(value).some(
      ([key, allowed]) => !catalog.has(key as Permission) || typeof allowed !== 'boolean',
    )
  )
    throw new Error('Неизвестное право или повреждённые персональные исключения.');
  return Object.fromEntries(
    Object.entries(value).sort(([a], [b]) => a.localeCompare(b)),
  ) as PermissionOverrides;
}

export function validateAccessPolicy(value: unknown): AccessPolicy {
  if (
    !value ||
    typeof value !== 'object' ||
    !('grantId' in value) ||
    typeof value.grantId !== 'string' ||
    !value.grantId ||
    !('revision' in value) ||
    !Number.isSafeInteger(value.revision) ||
    Number(value.revision) < 0 ||
    !('overrides' in value)
  )
    throw new Error('Настройки прав неполные или повреждены. Обновите страницу.');
  return {
    grantId: value.grantId,
    revision: Number(value.revision),
    overrides: validateOverrides(value.overrides),
  };
}

export function permissionDecision(subject: PermissionSubject | null | undefined, key: Permission) {
  const entry = catalog.get(key);
  if (!entry) return { allowed: false, reason: 'Неизвестное право' };
  if (!subject) return { allowed: false, reason: 'Нет данных о правах' };
  if (subject.role === 'superadmin') return { allowed: true, reason: 'Суперадминистратор' };
  if (!subject.access || !['viewer', 'editor'].includes(subject.role))
    return { allowed: false, reason: 'Нет данных о правах' };
  if (entry.operation !== 'view' && !permissionDecision(subject, `${entry.section}.view`).allowed)
    return { allowed: false, reason: 'Нет просмотра раздела' };
  const override = subject.access.overrides[key];
  return {
    allowed: override ?? (subject.role === 'editor' || entry.viewer),
    reason: override === undefined ? 'По базовой роли' : 'Персональное исключение',
  };
}
export function hasPermission(
  subject: PermissionSubject | null | undefined,
  ...keys: Permission[]
): boolean {
  return keys.length > 0 && keys.every((key) => permissionDecision(subject, key).allowed);
}
export function donorBulkPermission(action: unknown): Permission | null {
  return action === 'archive' || action === 'delete'
    ? 'donors.delete'
    : action === 'restore'
      ? 'donors.edit'
      : action === 'recheck'
        ? 'donors.run'
        : null;
}

export function aiReviewPermissions(category: unknown): Permission[] {
  // Confirming a competitor is an upsert in the registry, in addition to a SERP review.
  return category === 'competitor'
    ? ['fakes.edit', 'registry.create', 'registry.edit']
    : ['fakes.edit'];
}
